import { ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";

import { Badge } from "@/components/ui/badge";
import {
  ACCESS_REQUEST_SLA_HOURS,
  MILLISECONDS_PER_HOUR,
} from "@/constants/access-request";
import { ACCESS_REQUEST_STATUS_BADGE } from "@/constants/messages";
import { StatusAccessREquest } from "@/enum/status";
import { AccessRequestDTO } from "@/services/dtos/accessRequest.dto";
import { asKnownStatus } from "@/utils/access-request";

import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";

// Mapeamento de IDs de colunas para campos do banco
export const COLUMN_TO_DB_FIELD: Record<string, string> = {
  Nome: "name",
  cpf: "cpf",
  store: "store",
  "Data Criação": "created_at",
  status: "status",
  sla: "created_at",
};

export const columns: ColumnDef<AccessRequestDTO>[] = [
  {
    id: "Nome",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
  },
  {
    id: "store",
    accessorKey: "store",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
    cell: ({ row }) => `Loja ${row.original.store}`,
  },
  {
    id: "sla",
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="SLA" />
    ),
    cell: ({ row }) => {
      const rowStatus = asKnownStatus(row.original.status);
      const createdAt = row.original.created_at;

      if (!rowStatus || !createdAt) return null;
      if (rowStatus !== StatusAccessREquest.PENDING) return null;

      const createdDate = parseISO(createdAt);
      if (isNaN(createdDate.getTime())) return null;

      const remainingHours =
        ACCESS_REQUEST_SLA_HOURS -
        Math.floor(
          (new Date().getTime() - createdDate.getTime()) /
            MILLISECONDS_PER_HOUR,
        );

      const displayHours = Math.abs(remainingHours);
      const prefix = remainingHours < 0 ? "-" : "";
      const badgeColor =
        remainingHours >= 0
          ? "bg-green-500 text-white hover:bg-green-600"
          : "bg-red-500 text-white hover:bg-red-600";

      return <Badge className={badgeColor}>{`${prefix}${displayHours}h`}</Badge>;
    },
  },
  {
    id: "cpf",
    accessorKey: "cpf",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CPF" />
    ),
    cell: ({ row }) =>
      row.original.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"),
  },
  {
    id: "status",
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const statusValue = asKnownStatus(row.original.status);

      if (!statusValue) {
        return <Badge variant="default">Status Desconhecido</Badge>;
      }

      const currentStatus = ACCESS_REQUEST_STATUS_BADGE[statusValue];

      return (
        <Badge variant={currentStatus?.variant || "default"}>
          {currentStatus?.label || "Status Desconhecido"}
        </Badge>
      );
    },
  },
  {
    id: "Data Criação",
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const date = row.original.created_at;
      if (date) {
        const parsedDate = new Date(date);
        const formattedDate = format(parsedDate, "dd/MM/yyyy");
        return formattedDate;
      }
      return "Sem data";
    },
  },
  {
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
